import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { autoTune } from '../../../services/api';
import type {
  WorkflowEvaluationJob,
  WorkflowTuningChange,
  WorkflowTuningJob,
  WorkflowTuningJournal,
  WorkflowTuningUsage,
} from '../../../types/workflowTuning';
import { isWorkflowJobTerminal } from '../../../types/workflowTuning';
import type { WorkflowEditBridge } from '../../../types/workflowBridge';
import {
  applyChangesToStates,
  buildWorkflowEvaluationRequest,
  buildWorkflowTuningRequest,
  candidateVerdict,
  describeUnknownStatesRefusal,
  isApplicableChange,
  parseUnknownStatesRefusal,
  type WorkflowTuningForm,
} from '../../../utils/workflowTuningSpec';
import { getErrorMessage } from '../../../utils/errorUtils';
import { getAvailableModelsForConfigurator } from '../../../utils/configuratorModels';
import { formatTimestamp } from '../../../utils/workflowDsl';
import WorkflowTuningLaunch from './WorkflowTuningLaunch';
import WorkflowTuningChanges from './WorkflowTuningChanges';
import WorkflowTuningCharts from './WorkflowTuningCharts';
import WorkflowCompareView from './WorkflowCompareView';
import { Banner, Pill, Section } from './WorkflowFormControls';

export interface WorkflowTuningPageProps {
  integrationId: string;
  bridge: WorkflowEditBridge | null;
  datasets: string[];
  datasetsLoading?: boolean;
  onNotify: (type: 'success' | 'error' | 'info', message: string) => void;
  /** Дообучение моделей агентов — соседний путь оптимизации, и запускать его
   *  отсюда удобнее, чем возвращаться на вкладку Integrations. */
  onOpenFineTuning?: () => void;
}

/** Long jobs: a full-graph step runs about an hour, so polling is unhurried. */
const POLL_MS = 15_000;

/** The ml-service's own diagnosis, which used to collapse into a generic 503. */
function errorDetails(error: unknown): string | null {
  const details = (error as { response?: { data?: { error?: { details?: Record<string, unknown> } } } })?.response?.data
    ?.error?.details;
  if (!details || typeof details !== 'object') return null;
  const parts = Object.entries(details).map(([k, v]) => `${k}: ${String(v)}`);
  return parts.length ? parts.join(' · ') : null;
}

function skippedTargetView(value: unknown, index: number): { title: string; facts: string[] } {
  if (typeof value === 'string') return { title: value, facts: [] };
  if (!value || typeof value !== 'object' || Array.isArray(value)) return { title: `Target ${index + 1}`, facts: [String(value ?? 'Unknown target')] };
  const record = value as Record<string, unknown>;
  const preferred = ['path', 'target', 'state_id', 'state', 'name', 'id'];
  const titleKey = preferred.find((key) => typeof record[key] === 'string' && String(record[key]).trim());
  const readable = (item: unknown): string => {
    if (Array.isArray(item)) return item.map(readable).join(', ');
    if (item && typeof item === 'object') {
      return Object.entries(item as Record<string, unknown>)
        .map(([key, nested]) => `${key.replace(/[_-]+/g, ' ')}: ${readable(nested)}`)
        .join(' · ');
    }
    return String(item ?? '—');
  };
  const facts = Object.entries(record)
    .filter(([key, item]) => key !== titleKey && item !== undefined && item !== null)
    .slice(0, 4)
    .map(([key, item]) => `${key.replace(/[_-]+/g, ' ')}: ${readable(item)}`);
  return { title: titleKey ? String(record[titleKey]) : `Target ${index + 1}`, facts };
}

const STATUS_TONE: Record<string, 'ok' | 'warn' | 'danger' | 'accent' | 'neutral'> = {
  succeeded: 'ok',
  running: 'accent',
  queued: 'neutral',
  cancelling: 'warn',
  cancelled: 'neutral',
  failed: 'danger',
};

const WorkflowTuningPage: React.FC<WorkflowTuningPageProps> = ({
  integrationId,
  bridge,
  datasets,
  datasetsLoading,
  onNotify,
  onOpenFineTuning,
}) => {
  const [jobs, setJobs] = useState<WorkflowTuningJob[]>([]);
  const [evalJobs, setEvalJobs] = useState<WorkflowEvaluationJob[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [job, setJob] = useState<WorkflowTuningJob | null>(null);
  const [changes, setChanges] = useState<WorkflowTuningChange[]>([]);
  const [changesLoading, setChangesLoading] = useState(false);
  const [journal, setJournal] = useState<WorkflowTuningJournal | null>(null);
  const [usage, setUsage] = useState<WorkflowTuningUsage | null>(null);
  const [selectedPaths, setSelectedPaths] = useState<Set<string>>(new Set());
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [launchOpen, setLaunchOpen] = useState(true);

  /** The integration's own models, so the LLM judge picks from the same list as elsewhere. */
  const availableModels = useMemo(() => getAvailableModelsForConfigurator(integrationId), [integrationId]);

  const notifyRef = useRef(onNotify);
  notifyRef.current = onNotify;

  const refreshList = useCallback(async () => {
    try {
      const [opt, evalRes] = await Promise.all([
        autoTune.listWorkflowTuningJobs({ limit: 20 }),
        autoTune.listWorkflowEvaluationJobs({ limit: 10 }),
      ]);
      if (opt.success && opt.data) {
        setJobs((opt.data.items || []).filter((j) => !j.integration_id || j.integration_id === integrationId));
      }
      if (evalRes.success && evalRes.data) {
        setEvalJobs((evalRes.data.items || []).filter((j) => !j.integration_id || j.integration_id === integrationId));
      }
    } catch (e) {
      setError(getErrorMessage(e, 'Failed to load workflow tuning jobs'));
    }
  }, [integrationId]);

  useEffect(() => {
    setJobs([]);
    setEvalJobs([]);
    setSelectedId(null);
    setJob(null);
    setChanges([]);
    setJournal(null);
    setUsage(null);
    void refreshList();
  }, [refreshList]);

  /** Load the detail of one job; `changes`/`journal`/`usage` only once it succeeded. */
  const loadJob = useCallback(async (jobId: string) => {
    try {
      const res = await autoTune.getWorkflowTuningJob(jobId);
      if (!res.success || !res.data) return;
      const next = res.data;
      setJob(next);
      if (next.status === 'succeeded' && next.has_proposal) {
        setChangesLoading(true);
        try {
          const [ch, jr, us] = await Promise.all([
            autoTune.getWorkflowTuningChanges(jobId),
            autoTune.getWorkflowTuningJournal(jobId),
            autoTune.getWorkflowTuningUsage(jobId),
          ]);
          if (ch.success && ch.data) {
            setChanges(ch.data);
            // Everything applicable starts selected; globals cannot be written at all.
            setSelectedPaths(new Set(ch.data.filter(isApplicableChange).map((c) => c.path)));
          }
          if (jr.success && jr.data) setJournal(jr.data);
          if (us.success && us.data) setUsage(us.data);
        } finally {
          setChangesLoading(false);
        }
      }
    } catch (e) {
      setError(getErrorMessage(e, 'Failed to load the job'));
    }
  }, []);

  useEffect(() => {
    if (!selectedId) return;
    setChanges([]);
    setJournal(null);
    setUsage(null);
    void loadJob(selectedId);
  }, [selectedId, loadJob]);

  // Poll while anything is still moving.
  useEffect(() => {
    const live =
      jobs.some((j) => !isWorkflowJobTerminal(j.status)) ||
      evalJobs.some((j) => !isWorkflowJobTerminal(j.status)) ||
      (job != null && !isWorkflowJobTerminal(job.status));
    if (!live) return undefined;
    const timer = window.setInterval(() => {
      void refreshList();
      if (selectedId) void loadJob(selectedId);
    }, POLL_MS);
    return () => window.clearInterval(timer);
  }, [jobs, evalJobs, job, selectedId, refreshList, loadJob]);

  const startTuning = async (form: Omit<WorkflowTuningForm, 'integrationId'>) => {
    setSubmitting(true);
    setError(null);
    try {
      const res = await autoTune.createWorkflowTuningJob(buildWorkflowTuningRequest({ ...form, integrationId }));
      if (res.success && res.data) {
        notifyRef.current('success', 'Workflow tuning started.');
        setLaunchOpen(false);
        setSelectedId(res.data.id);
        await refreshList();
      } else {
        setError(res.error?.message || 'Failed to start workflow tuning');
      }
    } catch (e) {
      // Отказ по неизвестным состояниям объясняется по именам, а не общей
      // «не удалось запустить»: чинится он выбором другого состояния.
      const refusal = parseUnknownStatesRefusal(e);
      if (refusal) {
        setError(
          `The graph has no such state: ${describeUnknownStatesRefusal(refusal).join('; ')}. `
          + 'Refresh the graph and pick the state again.'
        );
      } else {
        const details = errorDetails(e);
        setError(`${getErrorMessage(e, 'Failed to start workflow tuning')}${details ? ` — ${details}` : ''}`);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const startEvaluation = async (form: {
    datasetNames: string[];
    batchSize?: number;
    evaluationMode?: string;
    evaluatorParams?: Record<string, unknown>;
  }) => {
    setSubmitting(true);
    setError(null);
    try {
      const res = await autoTune.createWorkflowEvaluationJob(
        buildWorkflowEvaluationRequest({ ...form, integrationId })
      );
      if (res.success) {
        notifyRef.current('success', 'Workflow evaluation started.');
        await refreshList();
      } else {
        setError(res.error?.message || 'Failed to start workflow evaluation');
      }
    } catch (e) {
      const details = errorDetails(e);
      setError(`${getErrorMessage(e, 'Failed to start workflow evaluation')}${details ? ` — ${details}` : ''}`);
    } finally {
      setSubmitting(false);
    }
  };

  const applySelected = () => {
    if (!bridge) return;
    const states = bridge.getStates();
    if (!states) {
      setError('Load the workflow graph before applying a proposal.');
      return;
    }
    const digest = job?.base_revision?.digest;
    if (digest && bridge.baseRevision && digest !== bridge.baseRevision) {
      setError(
        `This proposal was built on revision ${digest.slice(0, 12)}…, but the graph now sits on ${bridge.baseRevision.slice(0, 12)}…. ` +
          'Refresh the graph and re-run the tuning, or applying will be rejected with 409.'
      );
      return;
    }

    const picked = changes.filter((c) => selectedPaths.has(c.path) && isApplicableChange(c));
    const result = applyChangesToStates(states, picked);
    if (result.applied.length === 0) {
      setError('None of the selected changes could be placed in the current graph.');
      return;
    }
    bridge.applyProposedStates(result.states);
    bridge.focusWorkflow();
    notifyRef.current(
      'success',
      `Loaded ${result.applied.length} change${result.applied.length === 1 ? '' : 's'} into the graph as unsaved edits. Press “Save…” to apply.` +
        (result.missed.length ? ` ${result.missed.length} could not be placed — the graph moved.` : '')
    );
  };

  const toggle = (path: string) =>
    setSelectedPaths((prev) => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });

  const toggleGroup = (paths: string[], on: boolean) =>
    setSelectedPaths((prev) => {
      const next = new Set(prev);
      paths.forEach((p) => (on ? next.add(p) : next.delete(p)));
      return next;
    });

  const verdict = candidateVerdict(job?.summary?.candidate_accepted, job?.scores);
  /** Digest the proposal was built on, when the graph has since moved off it. */
  const staleRevision = useMemo(() => {
    const digest = job?.base_revision?.digest;
    if (!digest || !bridge?.baseRevision) return null;
    return digest !== bridge.baseRevision ? digest : null;
  }, [job, bridge]);
  const applicableSelected = useMemo(
    () => changes.filter((c) => selectedPaths.has(c.path) && isApplicableChange(c)).length,
    [changes, selectedPaths]
  );

  return (
    <div className="cwf-page" data-testid="cwf-tuning-page">
      <div className="cwf-page__inner">
        <header className="cwf-page__head cwt-page-hero">
          <div className="cwf-page__title">
            <span className="cwf-page__badge" aria-hidden="true">
              <i className="fas fa-wand-magic-sparkles" />
            </span>
            <div className="cwf-page__titletext">
              <span className="cwf-page__eyebrow">AI Tuner · Optimization workspace</span>
              <h2>Workflow optimization</h2>
            </div>
          </div>
          <p className="cwf-page__intro">
            Improves the whole dialogue graph with the same algorithms as config tuning, one state per internal
            iteration. The backend proposes; the change is applied here, through the graph's own Save flow, against the
            revision the proposal was built on.
          </p>
          <div className="cwt-page-hero__facts" aria-label="Optimization workspace overview">
            <span>
              <i className="fas fa-database" aria-hidden="true" />
              <strong>{datasets.length}</strong> datasets
            </span>
            <span>
              <i className="fas fa-diagram-project" aria-hidden="true" />
              <strong>{bridge?.stateIds.length ?? 0}</strong> states
            </span>
            <span>
              <i className="fas fa-clock-rotate-left" aria-hidden="true" />
              <strong>{jobs.length}</strong> runs
            </span>
          </div>
        </header>

        {error ? (
          <Banner tone="error" title="Workflow tuning" onDismiss={() => setError(null)}>
            <p>{error}</p>
          </Banner>
        ) : null}

        <div className="cwt-launch-card">
          <Section title="Configure a new run" defaultOpen={launchOpen} summary={`${datasets.length} datasets available`}>
            <WorkflowTuningLaunch
              integrationId={integrationId}
              graphStateIds={bridge?.stateIds ?? []}
              datasets={datasets}
              datasetsLoading={datasetsLoading}
              submitting={submitting}
              onSubmit={startTuning}
              onSubmitEvaluation={startEvaluation}
              onOpenFineTuning={onOpenFineTuning}
              availableModels={availableModels}
            />
          </Section>
        </div>

        <div className="cwt-columns">
          <aside className="cwt-list">
            <div className="cwt-list__head">
              <span className="cwt-list__title"><i className="fas fa-clock-rotate-left" aria-hidden="true" /> Run history</span>
              <button type="button" className="cwf-btn cwf-btn--xs" onClick={() => void refreshList()}>
                <i className="fas fa-sync-alt" aria-hidden="true" /> Refresh
              </button>
            </div>
            {jobs.length === 0 && evalJobs.length === 0 ? (
              <div className="cwt-list__empty"><i className="fas fa-wand-magic-sparkles" aria-hidden="true" /><span>No workflow runs yet</span><small>Your optimization history will appear here.</small></div>
            ) : null}
            {jobs.map((j) => (
              <button
                key={j.id}
                type="button"
                className={`cwt-job ${selectedId === j.id ? 'cwt-job--active' : ''}`}
                onClick={() => setSelectedId(j.id)}
              >
                <div className="cwt-job__top">
                  <Pill tone={STATUS_TONE[String(j.status)] || 'neutral'}>{j.status}</Pill>
                  <span className="cwt-job__approach">{j.approach}</span>
                  <i className="fas fa-chevron-right cwt-job__chevron" aria-hidden="true" />
                </div>
                <div className="cwt-job__meta">
                  {j.state_ids && j.state_ids.length > 0 ? `${j.state_ids.length} states` : 'whole graph'} ·{' '}
                  {formatTimestamp(j.created_at)}
                </div>
                {!isWorkflowJobTerminal(j.status) ? (
                  <div className="cwt-progress">
                    <div className="cwt-progress__bar" style={{ width: `${Math.min(100, j.progress_percent ?? 0)}%` }} />
                  </div>
                ) : null}
              </button>
            ))}
            {evalJobs.length > 0 ? <span className="cwt-list__subhead">Evaluations</span> : null}
            {evalJobs.map((j) => (
              <div key={j.id} className="cwt-job cwt-job--eval">
                <div className="cwt-job__top">
                  <Pill tone={STATUS_TONE[String(j.status)] || 'neutral'}>{j.status}</Pill>
                  <span className="cwt-job__approach">evaluation</span>
                </div>
                <div className="cwt-job__meta">
                  {/* The score is nested under result.evaluation, not at the root. */}
                  {typeof j.result?.evaluation?.score === 'number'
                    ? `score ${j.result.evaluation.score.toFixed(4)}`
                    : formatTimestamp(j.created_at)}
                </div>
              </div>
            ))}
          </aside>

          <main className={`cwt-detail ${job ? 'cwt-detail--filled' : 'cwt-detail--empty'}`}>
            {!job ? (
              <div className="cwt-detail__placeholder">
                <span>
                  <i className="fas fa-chart-line" aria-hidden="true" />
                </span>
                <h3>Run insights</h3>
                <p>Select a run to review quality, proposed changes and resource usage.</p>
              </div>
            ) : (
              <>
                <div className="cwt-detail__head">
                  <span className="cwt-detail__run-icon"><i className="fas fa-chart-line" aria-hidden="true" /></span>
                  <span className="cwt-detail__run"><strong>Optimization run</strong><code>{job.id}</code></span>
                  <Pill tone={STATUS_TONE[String(job.status)] || 'neutral'}>{job.status}</Pill>
                  {job.phase ? <span className="cwf-muted">{job.phase}</span> : null}
                  <span className="cwf-muted">
                    step {job.current_step ?? 0}/{job.total_steps ?? '—'}
                  </span>
                  {!isWorkflowJobTerminal(job.status) ? (
                    <button
                      type="button"
                      className="cwf-btn cwf-btn--xs cwf-btn--danger"
                      onClick={async () => {
                        await autoTune.cancelWorkflowTuningJob(job.id);
                        void loadJob(job.id);
                      }}
                    >
                      Cancel
                    </button>
                  ) : null}
                </div>

                {!isWorkflowJobTerminal(job.status) && job.runner_progress ? (
                  <div className="cwt-runner">
                    <div className="cwt-progress">
                      <div
                        className="cwt-progress__bar"
                        style={{ width: `${Math.min(100, job.runner_progress.progress_percent ?? 0)}%` }}
                      />
                    </div>
                    <span className="cwf-muted">
                      iteration {job.runner_progress.iteration ?? '—'}/{job.runner_progress.max_iterations ?? '—'}
                      {job.runner_progress.stage ? ` · ${job.runner_progress.stage}` : ''}
                      {job.runner_progress.message ? ` — ${job.runner_progress.message}` : ''}
                    </span>
                    <p className="cwf-note">
                      A step contains one iteration per state plus a closing one for the global sections, so the outer
                      step counter barely moves while this bar does.
                    </p>
                  </div>
                ) : null}

                {job.error ? (
                  <Banner tone="error" title="The run failed">
                    <p>{job.error}</p>
                  </Banner>
                ) : null}

                {job.summary ? (
                  <div className="cwt-summary">
                    <div className="cwt-stat">
                      <span className="cwt-stat__icon">
                        <i className="fas fa-pen-ruler" aria-hidden="true" />
                      </span>
                      <span className="cwt-stat__content">
                        <span className="cwt-stat__label">Proposed</span>
                        <span className="cwt-stat__value">
                          {job.summary.proposed_states_changed ?? 0} states · {job.summary.proposed_roles_changed ?? 0} roles
                        </span>
                      </span>
                    </div>
                    <div className="cwt-stat">
                      <span className="cwt-stat__icon">
                        <i className="fas fa-circle-check" aria-hidden="true" />
                      </span>
                      <span className="cwt-stat__content">
                        <span className="cwt-stat__label">Selection</span>
                        <span className="cwt-stat__value">
                          {verdict === 'unknown' ? (
                            <Pill tone="neutral">no selection info</Pill>
                          ) : verdict === 'rejected' ? (
                            <Pill tone="warn">candidate rejected</Pill>
                          ) : verdict === 'accepted-worse' ? (
                            <Pill tone="warn">accepted, quality fell</Pill>
                          ) : (
                            <Pill tone="ok">accepted</Pill>
                          )}
                        </span>
                      </span>
                    </div>
                    {job.scores ? (
                      <div className="cwt-stat">
                        <span className="cwt-stat__icon">
                          <i className="fas fa-arrow-trend-up" aria-hidden="true" />
                        </span>
                        <span className="cwt-stat__content">
                          <span className="cwt-stat__label">Quality</span>
                          <span className="cwt-stat__value">
                            {job.scores.initial?.toFixed(4)} → {job.scores.best?.toFixed(4)}{' '}
                            <span className={(job.scores.delta ?? 0) >= 0 ? 'cwt-delta--up' : 'cwt-delta--down'}>
                              {(job.scores.delta ?? 0) >= 0 ? '+' : ''}
                              {job.scores.delta?.toFixed(4)}
                            </span>
                          </span>
                        </span>
                      </div>
                    ) : null}
                    <div className="cwt-stat">
                      <span className="cwt-stat__icon">
                        <i className="fas fa-coins" aria-hidden="true" />
                      </span>
                      <span className="cwt-stat__content">
                        <span className="cwt-stat__label">Tokens</span>
                        <span className="cwt-stat__value">{(job.summary.tokens ?? 0).toLocaleString()}</span>
                      </span>
                    </div>
                  </div>
                ) : null}

                {verdict === 'rejected' || job.summary?.patch_limit_breached ? (
                  <Banner tone="warn" title="The proposal holds the original workflow">
                    <p>
                      {job.summary?.patch_limit_breached
                        ? 'The limiter fired, so nothing from this run was kept.'
                        : 'The candidate was not selected — the quality gain fell below the threshold, so the proposal is the graph as it already is.'}
                    </p>
                  </Banner>
                ) : null}

                {journal?.skipped_targets && journal.skipped_targets.length > 0 ? (
                  <section className="cwt-skipped">
                    <header className="cwt-skipped__head">
                      <span className="cwt-skipped__icon">
                        <i className="fas fa-exclamation-triangle" aria-hidden="true" />
                      </span>
                      <div>
                        <strong>
                          {journal.skipped_targets.length} requested target
                          {journal.skipped_targets.length === 1 ? ' was' : 's were'} never tuned
                        </strong>
                        <p>These targets were requested but the optimizer did not reach them during this run.</p>
                      </div>
                    </header>
                    <div className="cwt-skipped__grid">
                      {journal.skipped_targets.map((target, index) => {
                        const view = skippedTargetView(target, index);
                        return (
                          <article className="cwt-skipped__item" key={`${view.title}-${index}`}>
                            <span className="cwt-skipped__number">{index + 1}</span>
                            <div>
                              <code>{view.title}</code>
                              {view.facts.map((fact) => (
                                <span key={fact}>{fact}</span>
                              ))}
                            </div>
                          </article>
                        );
                      })}
                    </div>
                  </section>
                ) : null}

                {job.status === 'succeeded' && job.has_proposal ? (
                  <>
                    <div className="cwt-apply">
                      <span className="cwt-apply__copy">
                        <i className="fas fa-layer-group" aria-hidden="true" />
                        <span>
                          <strong>Review complete</strong>
                          <small>
                            {applicableSelected} of {changes.filter(isApplicableChange).length} applicable changes selected
                          </small>
                        </span>
                      </span>
                      <button
                        type="button"
                        className="cwf-btn cwf-btn--sm cwf-btn--primary"
                        disabled={applicableSelected === 0 || !bridge}
                        onClick={applySelected}
                      >
                        <i className="fas fa-download" aria-hidden="true" /> Load into graph
                      </button>
                    </div>
                    <WorkflowCompareView
                      integrationId={integrationId}
                      states={bridge?.getStates() ?? null}
                      changes={changes}
                      loading={changesLoading}
                      staleRevision={staleRevision}
                      workflowVersionId={job.base_revision?.workflow_version_id}
                    />
                    <WorkflowTuningChanges
                      changes={changes}
                      loading={changesLoading}
                      selected={selectedPaths}
                      onToggle={toggle}
                      onToggleGroup={toggleGroup}
                    />
                  </>
                ) : null}

                {journal?.slice_iterations && journal.slice_iterations.length > 0 ? (
                  <Section title="Journal" summary={`${journal.slice_iterations.length} iterations`}>
                    <table className="cwf-table">
                      <thead>
                        <tr>
                          <th>Slice</th>
                          <th>Changed</th>
                          <th>Tokens</th>
                          <th>Recovery</th>
                        </tr>
                      </thead>
                      <tbody>
                        {journal.slice_iterations.map((slice, i) => {
                          const lost = (slice.recovery?.discarded ?? 0) + (slice.recovery?.rejected ?? 0);
                          return (
                            <tr key={`${slice.slice}-${i}`}>
                              <td>
                                <code>{slice.slice}</code>
                                {slice.error ? <Pill tone="danger">error</Pill> : null}
                              </td>
                              <td>
                                {slice.changed_paths ?? 0}/{slice.paths ?? 0}
                              </td>
                              <td>{(slice.tokens ?? 0).toLocaleString()}</td>
                              <td>
                                {lost > 0 ? (
                                  <Pill tone="warn" title="Path keys the model mangled beyond recovery">
                                    {lost} lost
                                  </Pill>
                                ) : (
                                  <span className="cwf-muted">—</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </Section>
                ) : null}

                {usage?.operations && usage.operations.length > 0 ? (
                  <Section title="Usage" summary={`${(usage.total?.total_tokens ?? 0).toLocaleString()} tokens`}>
                    <table className="cwf-table">
                      <thead>
                        <tr>
                          <th>Operation</th>
                          <th>Tokens</th>
                          <th>Cost</th>
                        </tr>
                      </thead>
                      <tbody>
                        {usage.operations.map((op) => (
                          <tr key={op.operation}>
                            <td>{op.operation}</td>
                            <td>{(op.total_tokens ?? 0).toLocaleString()}</td>
                            <td>{op.total_cost != null ? `$${op.total_cost.toFixed(3)}` : '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </Section>
                ) : null}

                <WorkflowTuningCharts job={job} journal={journal} usage={usage} evalJobs={evalJobs} verdict={verdict} />
              </>
            )}
          </main>
        </div>
      </div>
    </div>
  );
};

export default WorkflowTuningPage;
